import { useMutation } from "@apollo/client"
import {
  BlocksApolloProvider,
  useBlocksContentOnly
} from "@pathwright/blocks-engine"
import { useEffect, useMemo, useState } from "react"
import { PathItemFragment } from "../api/generated"
import { useSyncPlanContext } from "../path/sync/SyncPlanContext"
import { useCanDesignSourcePath } from "../path/sync/utils"
import { getGraphQLEndpoint } from "../pathwright/PathwrightClient"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { flattenEdges } from "../utils/apollo"
import PUBLISH_BLOCKS_CONTENT from "./graphql/publish-blocks-content-mutation"
import { updateCachedPathItemBlocks } from "./utils"

type PublishData = {
  lastPublishedDateTime?: string
  lastModifiedDateTime: string
}

type PathItemSyncerProps = {
  pathItem: any
  onSyncItem: (pathItem: any, publishData: PublishData) => void
  onError: (pathItem: any, error: any) => void
}

const PathItemSyncer = ({
  pathItem,
  onSyncItem,
  onError
}: PathItemSyncerProps) => {
  const { data, error, loading } = useBlocksContentOnly({
    variables: {
      contextKey: `/${pathItem.source_id}`,
      mode: "EDIT",
      upsert: false,
      draft: true
    },
    skip: !pathItem.source_id
  })

  useEffect(() => {
    if (loading) return

    if (data?.content) {
      const { lastPublishedDateTime, lastModifiedDateTime } = data.content

      onSyncItem(pathItem, {
        lastPublishedDateTime: lastPublishedDateTime,
        lastModifiedDateTime: lastModifiedDateTime || new Date().toISOString()
      })
    } else if (pathItem.content_id) {
      // This scenario is common on staging, where the backend has a content record for the step
      // but there is no matching Firebase Blocks content
      // in this case we can just consider the item to be modified and not published
      onSyncItem(pathItem, {
        lastModifiedDateTime: new Date().toISOString()
      })
    } else {
      onError(pathItem, `No content found for path item. ${pathItem.id}`)
    }
  }, [data, loading])

  useEffect(() => {
    if (error) onError(pathItem, error)
  }, [error])

  return null
}

type ItemSyncedMap = {
  [key: string]: boolean
}

type PathBlocksContentSyncerProps = {
  pathItems: PathItemFragment[]
  cohortId: number
  onSync: (pathItems: PathItemFragment[], itemSyncedMap: ItemSyncedMap) => void
}

// TODO: move to server-side at some point.
// This component is responsible for syncing blocks content for path items that have blocks content.
const PathBlocksContentSyncer = ({
  pathItems,
  onSync,
  cohortId
}: PathBlocksContentSyncerProps) => {
  const { refetchSyncPlanQueries } = useSyncPlanContext()

  const context = usePathwrightContext()
  const graphQLEndpoint = `${getGraphQLEndpoint()}blocks/graphql?school_id=${
    context.school.id
  }`

  const userCanDesignSourcePath = useCanDesignSourcePath({
    cohortId
  })

  // Items that need to be synced (have blocks content but no content_last_modified)
  const items = useMemo(() => {
    return pathItems
      ? flattenEdges(pathItems).filter((item: any) => {
          const isSourced = item?.source_id !== item?.id

          if (isSourced && !userCanDesignSourcePath) return false

          return (
            item.type === "step" &&
            !!(
              item.content_id &&
              item.content_type === "blocks" &&
              !item.blocks?.content_last_modified
            )
          )
        })
      : null
  }, [pathItems])

  // Map of items and whether they've been synced
  const [itemSyncedMap, setItemSyncedMap] = useState<ItemSyncedMap>({})

  useEffect(() => {
    if (!items) return

    setItemSyncedMap(
      items.reduce((acc: ItemSyncedMap, pathItem: any) => {
        if (pathItem.content_id && !pathItem.blocks?.content_last_modified) {
          acc[pathItem.id] = false
        } else {
          acc[pathItem.id] = true
        }

        return acc
      }, {})
    )
  }, [items])

  useEffect(() => {
    const syncedBools = Object.values(itemSyncedMap)

    const allSynced =
      syncedBools.length === items?.length &&
      syncedBools.every((synced) => !!synced)
    if (allSynced) {
      onSync(items, itemSyncedMap)
    }
  }, [itemSyncedMap])

  const [updateBlocksContent] = useMutation(PUBLISH_BLOCKS_CONTENT)

  const handleSyncedItem = async (
    pathItem: any,
    { lastPublishedDateTime, lastModifiedDateTime }: PublishData
  ) => {
    if (!pathItem?.content_id) return

    const lastPublished = lastPublishedDateTime
      ? new Date(lastPublishedDateTime).toISOString()
      : null
    const contentLastModified = lastModifiedDateTime
      ? new Date(lastModifiedDateTime).toISOString()
      : null

    updateCachedPathItemBlocks(pathItem, {
      id: pathItem.content_id,
      type: "blocks",
      content_last_modified: contentLastModified,
      last_published: lastPublished
    })

    await updateBlocksContent({
      variables: {
        id: pathItem.content_id,
        stepId: pathItem.id,
        contentLastModified,
        lastPublished
      }
    })

    setItemSyncedMap((prev) => ({
      ...prev,
      [pathItem.id]: true
    }))

    // If all items are synced, refetch the sync plan queries
    if (
      refetchSyncPlanQueries &&
      Object.values(itemSyncedMap).length &&
      Object.values(itemSyncedMap).every((synced) => !!synced)
    ) {
      refetchSyncPlanQueries()
    }
  }

  const handleSyncError = (pathItem: any, error: any) => {
    console.log("PathItem Blocks couldn't sync: ", error)
    // We don't want a server error to prevent the rest of the items from syncing
    setItemSyncedMap((prev) => ({
      ...prev,
      [pathItem.id]: true
    }))
  }

  if (!items?.length) return null

  return (
    <BlocksApolloProvider
      endpoint={graphQLEndpoint}
      accountID={context.school.id}
    >
      {items.map((pathItem: any) => (
        <PathItemSyncer
          key={`path-item-syncer-${pathItem.id}`}
          pathItem={pathItem}
          onSyncItem={handleSyncedItem}
          onError={handleSyncError}
        />
      ))}
    </BlocksApolloProvider>
  )
}

export default PathBlocksContentSyncer
